import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CalendarDaysIcon,
    ChartBarIcon,
    BarsArrowDownIcon,
    DocumentArrowDownIcon,
    ArrowUpIcon,
    ArrowDownIcon
} from '@heroicons/react/24/outline';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Mock data for a full month of attendance
const mockAttendance = [
    { id: 1, name: 'Jane Doe', department: 'Technology', date: '2024-07-22', status: 'Present', checkIn: '09:00 AM', checkOut: '05:00 PM' },
    { id: 2, name: 'John Smith', department: 'Sales', date: '2024-07-22', status: 'Present', checkIn: '08:55 AM', checkOut: '05:10 PM' },
    { id: 3, name: 'Emily White', department: 'Human Resources', date: '2024-07-22', status: 'On Leave', checkIn: '-', checkOut: '-' },
    { id: 4, name: 'David Lee', department: 'Marketing', date: '2024-07-22', status: 'Present', checkIn: '09:15 AM', checkOut: '05:05 PM' },
    { id: 5, name: 'Sarah Chen', department: 'Technology', date: '2024-07-22', status: 'Present', checkIn: '08:45 AM', checkOut: '05:30 PM' },
    { id: 6, name: 'Michael Brown', department: 'Operations', date: '2024-07-22', status: 'Absent', checkIn: '-', checkOut: '-' },
    { id: 7, name: 'Jane Doe', department: 'Technology', date: '2024-07-21', status: 'Present', checkIn: '09:05 AM', checkOut: '05:05 PM' },
    { id: 8, name: 'John Smith', department: 'Sales', date: '2024-07-21', status: 'On Leave', checkIn: '-', checkOut: '-' },
    // A more extensive data set would be here
];

const mockAttendanceSummary = {
    totalEmployees: 250,
    present: 210,
    absent: 15,
    onLeave: 25,
};

const mockMonthlyData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
        {
            label: 'Total Present',
            data: [200, 210, 220, 205, 230, 240, 210],
            backgroundColor: 'rgba(75, 192, 192, 0.6)',
        },
        {
            label: 'Total Absent',
            data: [15, 10, 8, 12, 10, 5, 15],
            backgroundColor: 'rgba(255, 99, 132, 0.6)',
        },
    ],
};

const AttendanceReports = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [filterDate, setFilterDate] = useState('2024-07-22');

    const filteredRecords = useMemo(() => {
        return mockAttendance.filter(record =>
            record.date === filterDate &&
            (record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.department.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [filterDate, searchTerm]);

    const sortedRecords = useMemo(() => {
        let sortableRecords = [...filteredRecords];
        if (sortConfig.key) {
            sortableRecords.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableRecords;
    }, [filteredRecords, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <BarsArrowDownIcon className="w-4 h-4 ml-1 text-gray-400" />;
        return sortConfig.direction === 'ascending' ? <ArrowUpIcon className="w-4 h-4 ml-1" /> : <ArrowDownIcon className="w-4 h-4 ml-1" />;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-teal-100 to-lime-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header Section */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-green-400">
                    <h1 className="text-4xl font-extrabold text-green-800 tracking-tight mb-4 md:mb-0">
                        Attendance Reports
                    </h1>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <CalendarDaysIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-300 shadow-sm"
                            />
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
                        >
                            <DocumentArrowDownIcon className="h-5 w-5" />
                            <span className="font-semibold">Export Report</span>
                        </motion.button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Total Present</h3>
                        <p className="text-4xl font-extrabold text-teal-600 mt-2">{mockAttendanceSummary.present}</p>
                        <p className="text-sm text-gray-500">of {mockAttendanceSummary.totalEmployees} Employees</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Total Absent</h3>
                        <p className="text-4xl font-extrabold text-red-600 mt-2">{mockAttendanceSummary.absent}</p>
                        <p className="text-sm text-gray-500">as of today</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">On Leave</h3>
                        <p className="text-4xl font-extrabold text-yellow-600 mt-2">{mockAttendanceSummary.onLeave}</p>
                        <p className="text-sm text-gray-500">Approved leaves</p>
                    </motion.div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Attendance Trends Chart */}
                    <motion.div
                        className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-green-600">
                            <ChartBarIcon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Monthly Attendance Trends</h3>
                        </div>
                        <div className="h-72">
                            <Bar
                                data={mockMonthlyData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { position: 'top' },
                                        title: { display: false },
                                    },
                                    scales: {
                                        y: { beginAtZero: true },
                                    },
                                }}
                            />
                        </div>
                    </motion.div>

                    {/* Placeholder for future filter options */}
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-green-600">
                            <BarsArrowDownIcon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Filter Options</h3>
                        </div>
                        <div className="space-y-4">
                            <label className="block">
                                <span className="text-gray-700">Filter by Department:</span>
                                <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500">
                                    <option>All Departments</option>
                                    <option>Technology</option>
                                    <option>Sales</option>
                                    <option>Human Resources</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-gray-700">Filter by Status:</span>
                                <select className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500">
                                    <option>All Statuses</option>
                                    <option>Present</option>
                                    <option>Absent</option>
                                    <option>On Leave</option>
                                </select>
                            </label>
                        </div>
                    </motion.div>
                </div>

                {/* Attendance Records Table */}
                <motion.div
                    className="mt-8 bg-white rounded-3xl shadow-xl overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['name', 'department', 'date', 'status', 'checkIn', 'checkOut'].map(key => (
                                        <th
                                            key={key}
                                            onClick={() => requestSort(key)}
                                            className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center">
                                                {key.replace(/([A-Z])/g, ' $1').charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').slice(1)}
                                                {getSortIcon(key)}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <AnimatePresence initial={false}>
                                    {sortedRecords.length > 0 ? (
                                        sortedRecords.map(record => (
                                            <motion.tr
                                                key={record.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, x: -20, transition: { duration: 0.3 } }}
                                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.department}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.date}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        record.status === 'Present' ? 'bg-green-100 text-green-800' :
                                                        record.status === 'On Leave' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {record.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.checkIn}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.checkOut}</td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No attendance records found for this date.</td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default AttendanceReports;